import type { AssignmentCriterion, GradeLevel, PaperType, MatchingTemplate } from "@markeroo/shared";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/Modal";
import { SaveCriteriaTemplateModal } from "@/components/SaveCriteriaTemplateModal";
import { InfoBanner } from "@/components/InfoBanner";
import { buttonVariants } from "@markeroo/theme";
import { useState, useCallback } from "react";
import { AddCriteriaModal } from "./AddCriteriaModal";
import { CriteriaList } from "./CriteriaList";
import { CriteriaEditModal } from "./CriteriaEditModal";
import { useGenerateCriteria, useBulkCreateCriteria, useCreateCriterion, useDeleteCriterion, useResetCriteriaDefaults, useSaveAssignmentCriteriaAsCriteriaTemplate } from "@/api/hooks";
import { useNotification } from "@/providers/NotificationProvider";
import { notifications, ui, confirmations } from "@/strings";
import type { NotificationKind } from "@/providers/NotificationProvider";

interface CriteriaManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  assignmentId: string;
  assignmentName?: string;
  criteria: AssignmentCriterion[];
  gradeLevel?: GradeLevel | null;
  paperType?: PaperType | null;
  criteriaAreCustom?: boolean;
  matchingTemplate?: MatchingTemplate | null;
}

export function CriteriaManagerModal({ isOpen, onClose, assignmentId, assignmentName, criteria, gradeLevel, paperType, criteriaAreCustom, matchingTemplate }: CriteriaManagerModalProps) {
  const [editing, setEditing] = useState<AssignmentCriterion | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [highlightIds, setHighlightIds] = useState<Set<string>>(new Set());
  const [showInfoBanner, setShowInfoBanner] = useState(true);

  const generateMutation = useGenerateCriteria(assignmentId);
  const bulkCreateMutation = useBulkCreateCriteria(assignmentId);
  const createMutation = useCreateCriterion(assignmentId);
  const deleteMutation = useDeleteCriterion(assignmentId);
  const resetMutation = useResetCriteriaDefaults(assignmentId);
  const saveMutation = useSaveAssignmentCriteriaAsCriteriaTemplate(assignmentId);
  const { show } = useNotification();
  const TOAST_SUCCESS: NotificationKind = "success";
  const TOAST_ERROR: NotificationKind = "error";
  const TOAST_INFO: NotificationKind = "info";

  const handleGenerate = useCallback(async (prompt: string) => {
    try {
      const gradeLevelNumber = gradeLevel ? parseInt(gradeLevel) : null;
      const result = await generateMutation.mutateAsync({
        prompt,
        gradeLevel: gradeLevelNumber
      });
      if (!result?.criteria?.length) {
        show(notifications.criteria.noRulesGenerated, TOAST_INFO);
        throw new Error("No criteria generated");
      }

      // Build payload with action and key fields
      const payload = result.criteria.map((c: AssignmentCriterion) => ({
        label: c.label,
        llmDescription: c.llmDescription ?? "",
        humanDescription: c.description ?? "",
        action: c.action,
        key: c.key,
      }));

      const bulk = await bulkCreateMutation.mutateAsync(payload);
      const failedCount = bulk?.failed?.length ?? 0;

      // Use IDs from bulk creation response which are the real DB IDs
      const realIds = new Set(bulk?.created?.map(c => c.id) ?? []);

      // Highlight all affected criteria (new, matched, updated)
      setHighlightIds(realIds);

      // Count intents for notification message
      const intentCreate = result.criteria.filter(c => !c.action || c.action === 'create_new').length;
      const intentMatch = result.criteria.filter(c => c.action === 'matches_existing' || c.action === 'suggest_update').length;

      // Build notification message
      let message = "";
      if (intentCreate > 0 && intentMatch > 0) {
        message = `Created ${intentCreate} new ${intentCreate === 1 ? 'criterion' : 'criteria'} and matched ${intentMatch} existing ${intentMatch === 1 ? 'criterion' : 'criteria'}`;
      } else if (intentCreate > 0) {
        message = notifications.criteria.rulesAdded(intentCreate, failedCount);
      } else if (intentMatch > 0) {
        message = `Matched ${intentMatch} existing ${intentMatch === 1 ? 'criterion' : 'criteria'}`;
      }

      show(message, failedCount > 0 ? TOAST_ERROR : TOAST_SUCCESS);

      // Wait for React to render the updated criteria list before closing the modal
      // This ensures the new criteria card appears in the DOM before the tutorial proceeds
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      // Auto-close the add modal after successful generation
      setIsAddModalOpen(false);
    } catch (error) {
      // Only show error notification for actual failures, not for "no criteria generated"
      if (error instanceof Error && error.message !== "No criteria generated") {
        show(notifications.criteria.generateFailed, TOAST_ERROR);
      }
      throw error;
    }
  }, [gradeLevel, generateMutation, bulkCreateMutation, show]);

  const handleManualCreate = async (data: { label: string; humanDescription: string; llmDescription: string; category: string }) => {
    try {
      await createMutation.mutateAsync({
        label: data.label,
        llmDescription: data.llmDescription,
        humanDescription: data.humanDescription,
        category: data.category,
      });
      show(notifications.criteria.ruleCreated, TOAST_SUCCESS);
      setIsAddModalOpen(false);
    } catch {
      show(notifications.criteria.ruleSaveFailed, TOAST_ERROR);
      throw new Error("Failed to create criterion");
    }
  };

  const handleDelete = async (criterionKey: string) => {
    try {
      await deleteMutation.mutateAsync(criterionKey);
      show(notifications.criteria.ruleDeleted, TOAST_SUCCESS);
    } catch {
      show(notifications.criteria.ruleDeleteFailed, TOAST_ERROR);
    }
  };

  const handleReset = async () => {
    try {
      await resetMutation.mutateAsync();
      show(notifications.criteria.rulesReset, TOAST_SUCCESS);
      setConfirmResetOpen(false);
    } catch {
      show(notifications.criteria.rulesResetFailed, TOAST_ERROR);
    }
  };

  const handleSaveAsCriteria = async (data: {
    name: string;
    paperType: PaperType | null;
    grades: number[] | null;
    isDefaultForType: boolean;
  }) => {
    try {
      await saveMutation.mutateAsync(data);
      show(notifications.criteriaTemplates.created, TOAST_SUCCESS);
      setSaveModalOpen(false);
    } catch {
      show(notifications.criteriaTemplates.createFailed, TOAST_ERROR);
    }
  };

  // Early return AFTER all hooks to comply with Rules of Hooks
  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <ModalHeader
        title={
          <span className="font-semibold text-h1 leading-snug tracking-tight text-text-theme-primary">
            {/* eslint-disable-next-line no-restricted-syntax */}
            {ui.headers.criteriaTemplates} {ui.labels.for} {ui.labels.papers} in {assignmentName || ui.labels.assignment}
          </span>
        }
        onClose={onClose}
      />
      <ModalBody>
        {matchingTemplate && !criteriaAreCustom && showInfoBanner && (
          <InfoBanner
            title={`${ui.labels.usingDefaultCriteria} ${matchingTemplate.name}`}
            infoLines={[
              ui.labels.defaultCriteriaWork,
              ui.labels.pasteInstructionsOrSkills,
            ]}
            onDismiss={() => setShowInfoBanner(false)}
          />
        )}

        {/* Criteria Section */}
        <div className="flex-1 min-h-0 overflow-y-auto pt-6 pb-8 px-2">
          <CriteriaList
            criteria={criteria}
            onEdit={(c) => setEditing(c)}
            onDelete={(k) => {
              void handleDelete(k);
            }}
            onAddClick={() => setIsAddModalOpen(true)}
            isDeleting={deleteMutation.isPending}
            highlightIds={highlightIds}
            paperType={paperType}
            gradeLevel={gradeLevel}
          />
        </div>
      </ModalBody>
      {/* Footer Section - Figma spec: border E5E6E8, specific button styles */}
      <ModalFooter>
        <button
          type="button"
          className="bg-surface-button-secondary rounded-lg px-3 py-2.5 font-medium text-body leading-5 tracking-tight text-brand-950 hover:bg-surface-button-secondary-hover transition-colors"
          onClick={onClose}
          data-tutorial-target="criteria-manager-close-button"
        >
          {ui.actions.close}
        </button>
        {criteriaAreCustom && (
          <button
            type="button"
            className={buttonVariants.primary}
            onClick={() => setSaveModalOpen(true)}
          >
            {ui.actions.saveAsNewCriteria}
          </button>
        )}
        <button
          type="button"
          className="bg-white rounded-lg px-3 py-2.5 font-medium text-body leading-5 tracking-tight text-brand-950 hover:bg-surface-hover transition-colors"
          onClick={() => setConfirmResetOpen(true)}
        >
          {ui.actions.resetToDefaults}
        </button>
      </ModalFooter>

      {/* Nested modals */}
      <AddCriteriaModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onGenerate={handleGenerate}
        onManualCreate={handleManualCreate}
        isGenerating={generateMutation.isPending || bulkCreateMutation.isPending}
        isCreatingManual={createMutation.isPending}
        gradeLevel={gradeLevel}
      />

      <CriteriaEditModal
        isOpen={Boolean(editing)}
        criterion={editing}
        assignmentId={assignmentId}
        onClose={() => setEditing(null)}
      />

      {confirmResetOpen && (
        <Modal isOpen={confirmResetOpen} onClose={() => setConfirmResetOpen(false)} size="sm">
          <ModalHeader title={ui.actions.resetToDefaults} onClose={() => setConfirmResetOpen(false)} />
          <ModalBody>
            <p>{confirmations.criteria.reset}</p>
          </ModalBody>
          <ModalFooter>
            <button className={buttonVariants.secondary} onClick={() => setConfirmResetOpen(false)}>
              {ui.actions.cancel}
            </button>
            <button className={buttonVariants.danger} onClick={() => { void handleReset(); }}>
              {ui.actions.resetToDefaults}
            </button>
          </ModalFooter>
        </Modal>
      )}

      <SaveCriteriaTemplateModal
        isOpen={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        onSubmit={(data) => { void handleSaveAsCriteria(data); }}
        isSubmitting={saveMutation.isPending}
        defaultPaperType={paperType ?? null}
        defaultGrades={gradeLevel ? [parseInt(gradeLevel)] : null}
      />
    </Modal>
  );
}
